import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { fetchUserAttributes } from '@aws-amplify/auth';
import '@aws-amplify/ui-react/styles.css';

import Login from './components/Login';
import IDMatching from './components/IDMatching';
import Membership from './components/Membership';
import Admin from './components/AdminPanel';
import TempHome from './components/tempHome';
import FulfillmentTracker from './components/FulfillmentTracker';
import FulfillmentDashboard from './components/FulfillmentDashboard';
import Profile from './components/Profile';
import AccessDenied from './components/AccessDenied';
import ProtectedRoute from './utils/ProtectedRoute';
import PromotionsManager from './components/PromotionsManager';
import { DesktopSidebar, MobileTopBar, MobileSidebar, FULL_HEIGHT_ROUTES } from './components/NavSidebar';
import { ROUTE_GROUPS } from './utils/groups';
import { useGroups } from './utils/useGroups';

function AppLayout({ user, signOut, initialPath }) {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { groups } = useGroups();

  const userIdentifier = user?.signInDetails?.loginId || 'User';
  const [givenName,  setGivenName]  = useState('');
  const [familyName, setFamilyName] = useState('');

  // Amplify v6 — attributes aren't on the user object, must be fetched separately.
  // Re-runs when the user object changes (e.g. after sign-in or attribute update).
  useEffect(() => {
    fetchUserAttributes()
      .then(attrs => {
        setGivenName(attrs.given_name   || '');
        setFamilyName(attrs.family_name || '');
      })
      .catch(() => {
        // Silently fall back — initials will show '?' until resolved
      });
  }, [user]);

  const isFullHeight = FULL_HEIGHT_ROUTES.includes(location.pathname);

  return (
    <div className="flex flex-col lg:flex-row h-screen bg-gray-100 font-sans antialiased overflow-hidden">
      <MobileTopBar
        onMenuOpen={() => setMobileOpen(true)}
        userIdentifier={userIdentifier}
        givenName={givenName}
        familyName={familyName}
        signOut={signOut}
      />
      <MobileSidebar
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        userGroups={groups}
        groups={groups}
        userIdentifier={userIdentifier}
        givenName={givenName}
        familyName={familyName}
        signOut={signOut}
      />
      <DesktopSidebar
        collapsed={collapsed}
        onToggle={() => setCollapsed(c => !c)}
        userGroups={groups}
        groups={groups}
        userIdentifier={userIdentifier}
        givenName={givenName}
        familyName={familyName}
        signOut={signOut}
      />

      <main className={['flex-1 bg-white', isFullHeight ? 'overflow-hidden flex flex-col' : 'overflow-y-auto'].join(' ')}>
        <Routes>
          {initialPath && <Route path="/" element={<Navigate to={initialPath} replace />} />}

          <Route path="/" element={<div className="p-6 sm:p-8 lg:p-10"><TempHome /></div>} />
          <Route path="/profile" element={<div className="h-full flex flex-col overflow-hidden"><Profile /></div>} />
          <Route path="/access-denied" element={<div className="h-full flex flex-col"><AccessDenied /></div>} />

          <Route path="/fulfillment" element={
            <ProtectedRoute groups={ROUTE_GROUPS['/fulfillment']}>
              <div className="h-full flex flex-col"><FulfillmentTracker /></div>
            </ProtectedRoute>
          } />
          <Route path="/promotions" element={
            <ProtectedRoute groups={ROUTE_GROUPS['/promotions']}>
              <div className="h-full flex flex-col"><PromotionsManager /></div>
            </ProtectedRoute>
          } />
          <Route path="/dashboard" element={
            <ProtectedRoute groups={ROUTE_GROUPS['/dashboard']}>
              <div className="h-full flex flex-col"><FulfillmentDashboard /></div>
            </ProtectedRoute>
          } />
          <Route path="/events/id-matching" element={
            <ProtectedRoute groups={ROUTE_GROUPS['/events/id-matching']}>
              <div className="h-full flex flex-col"><IDMatching /></div>
            </ProtectedRoute>
          } />
          <Route path="/membership" element={
            <ProtectedRoute groups={ROUTE_GROUPS['/membership']}>
              <div className="h-full flex flex-col"><Membership /></div>
            </ProtectedRoute>
          } />
          <Route path="/aisrequests" element={
            <ProtectedRoute groups={ROUTE_GROUPS['/aisrequests']}>
              <div className="h-full flex flex-col"><div className="text-sm text-gray-400">AIS Requests form coming soon</div></div>
            </ProtectedRoute>
          } />
          <Route path="/admin" element={
            <ProtectedRoute groups={ROUTE_GROUPS['/admin']}>
              <Admin />
            </ProtectedRoute>
          } />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}

function App() {
  const { user, signOut } = useAuthenticator(context => [context.user]);
  if (!user) return <Login />;

  const initialPath = sessionStorage.getItem('redirectAfterLogin') || null;

  return (
    <Router>
      <AppLayout user={user} signOut={signOut} initialPath={initialPath} />
    </Router>
  );
}

export default App;